import { useState } from "react";
import {
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconBuilding, IconLogout, IconPlus, IconStar, IconStarFilled } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useCompanies,
  useCreateCompany,
  useSetDefaultCompany,
  useLeaveCompany,
  type Company,
  type CreateCompanyData,
} from "@/hooks/useCompanies";

export const Route = createFileRoute("/settings/company" as any)({
  component: CompanySettingsPage,
});

function CompanySettingsPage() {
  const { data, isLoading } = useCompanies();
  const createCompany = useCreateCompany();
  const setDefaultCompany = useSetDefaultCompany();
  const leaveCompany = useLeaveCompany();
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [leaveOpened, { open: openLeave, close: closeLeave }] = useDisclosure(false);
  const [companyToLeave, setCompanyToLeave] = useState<Company | null>(null);

  const companies = data?.companies || [];

  const createForm = useForm<CreateCompanyData>({
    initialValues: {
      name: "",
    },
    validate: {
      name: (value) => (!value ? "Company name is required" : null),
    },
  });

  const handleCreate = createForm.onSubmit(async (values) => {
    try {
      await createCompany.mutateAsync(values);
      notifications.show({
        title: "Success",
        message: "Company created successfully!",
        color: "green",
      });
      createForm.reset();
      closeCreate();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create company",
        color: "red",
      });
    }
  });

  const handleSetDefault = async (companyId: string) => {
    try {
      await setDefaultCompany.mutateAsync(companyId);
      notifications.show({
        title: "Success",
        message: "Default company updated successfully!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to set default company",
        color: "red",
      });
    }
  };

  const handleLeaveClick = (company: Company) => {
    setCompanyToLeave(company);
    openLeave();
  };

  const handleLeaveConfirm = async () => {
    if (!companyToLeave) return;

    try {
      await leaveCompany.mutateAsync(companyToLeave.id);
      notifications.show({
        title: "Success",
        message: "Left company successfully!",
        color: "green",
      });
      closeLeave();
      setCompanyToLeave(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to leave company",
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={2}>Company Management</Title>
                <Text size="sm" c="dimmed" mt="xs">
                  Manage your company associations
                </Text>
              </div>
              <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                Create Company
              </Button>
            </Group>

            {companies.length === 0 ? (
              <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
                <Stack gap="md" align="center">
                  <IconBuilding size={48} stroke={1.5} style={{ opacity: 0.5 }} />
                  <Text c="dimmed" ta="center" maw={600}>
                    You're not associated with any company yet. Companies are optional in Kablan - you
                    can use the platform independently or create/join a company.
                  </Text>
                  <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                    Create Company
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <>
                <Text size="sm" c="dimmed">
                  {companies.length} {companies.length === 1 ? "company" : "companies"}
                </Text>

                <Stack gap="md">
                  {companies.map((company) => (
                    <Paper key={company.id} p="md" withBorder>
                      <Group justify="space-between" align="center">
                        <Group gap="md">
                          <IconBuilding size={24} stroke={1.5} />
                          <div>
                            <Group gap="xs" align="center">
                              <Text fw={500}>{company.name}</Text>
                              {company.isDefault && (
                                <Badge variant="light" color="blue" size="sm" leftSection={<IconStarFilled size={12} />}>
                                  Default
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              Member since {new Date(company.createdAt).toLocaleDateString()}
                            </Text>
                          </div>
                        </Group>

                        <Group gap="xs">
                          {!company.isDefault && (
                            <Button
                              variant="light"
                              size="sm"
                              leftSection={<IconStar size={16} />}
                              onClick={() => handleSetDefault(company.id)}
                              loading={setDefaultCompany.isPending}
                            >
                              Set as Default
                            </Button>
                          )}
                          <Button
                            variant="subtle"
                            color="red"
                            size="sm"
                            leftSection={<IconLogout size={16} />}
                            onClick={() => handleLeaveClick(company)}
                          >
                            Leave
                          </Button>
                        </Group>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Create Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title="Create Company" centered>
        <form onSubmit={handleCreate}>
          <Stack gap="md">
            <TextInput
              label="Company Name"
              placeholder="e.g., ABC Construction"
              {...createForm.getInputProps("name")}
              required
              autoFocus
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeCreate} disabled={createCompany.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={createCompany.isPending}>
                Create Company
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Leave Confirmation Modal */}
      <Modal
        opened={leaveOpened}
        onClose={() => {
          closeLeave();
          setCompanyToLeave(null);
        }}
        title="Leave Company"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to leave &quot;{companyToLeave?.name}&quot;? You will lose access to all
            company projects and data. This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                closeLeave();
                setCompanyToLeave(null);
              }}
              disabled={leaveCompany.isPending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleLeaveConfirm} loading={leaveCompany.isPending}>
              Leave Company
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
